import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    useMediaQuery,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import { Delete, Visibility, CheckCircle, BusinessCenter, WarningAmber, Email } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from './config';
import { AdminPageShell, AdminStat, AdminSurface } from './admin/AdminPageShell';

const formatWarningLines = (warnings = []) => warnings.filter(Boolean);

const formatExpiryDate = (value) => {
    if (!value) return 'No expiry';
    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDaysToExpiry = (value) => {
    if (value === null || value === undefined) return 'No expiry';
    const days = Number(value);
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
    if (days === 0) return 'Expires today';
    return `${days} day${days === 1 ? '' : 's'}`;
};

const buildDeletionAlert = ({ responseMessage, fallbackMessage, warnings = [] }) => {
    const warningLines = formatWarningLines(warnings);

    return {
        severity: warningLines.length > 0 ? 'warning' : 'success',
        text: responseMessage || [fallbackMessage, ...warningLines].filter(Boolean).join(' '),
    };
};

const BusinessList = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [businesses, setBusinesses] = useState([]);
    const [totalBusinesses, setTotalBusinesses] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('');
    const [actionMessage, setActionMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notifyingBusinessId, setNotifyingBusinessId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(API_URL + 'get_businesses.php', {
                    params: { page: page + 1, limit: rowsPerPage },
                    withCredentials: true,
                });
                setBusinesses(response.data.data || []);
                setTotalBusinesses(response.data.total || 0);
            } catch (err) {
                setError('Failed to fetch businesses');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [page, rowsPerPage]);

    const approvedCount = useMemo(
        () => businesses.filter((business) => business.approved).length,
        [businesses],
    );

    const handleView = (id) => navigate(`/business/${id}`);

    const handleSendExpiryNotification = async (business) => {
        try {
            setNotifyingBusinessId(business.id);
            setActionMessage(null);

            const response = await axios.post(
                API_URL + 'send_expiry_notification.php',
                { business_id: business.id },
                { withCredentials: true },
            );

            setActionMessage({
                severity: response.data?.success ? 'success' : 'error',
                text: response.data?.message || 'Expiry notification request completed.',
            });
        } catch (err) {
            setActionMessage({
                severity: 'error',
                text: err.response?.data?.message || 'Failed to send expiry notification.',
            });
        } finally {
            setNotifyingBusinessId(null);
        }
    };

    const closeDialog = useCallback(() => {
        setOpenDialog(false);
        setSelectedBusiness(null);
        setDialogType('');
    }, []);

    const handleDelete = async () => {
        if (!selectedBusiness) {
            return;
        }

        const businessToDelete = selectedBusiness;

        try {
            setSubmitting(true);
            setActionMessage(null);

            const response = await axios.post(
                API_URL + 'delete_business.php',
                { data: { id: businessToDelete.id } },
                { withCredentials: true },
            );

            setBusinesses((prev) => prev.filter((business) => business.id !== businessToDelete.id));
            setTotalBusinesses((prev) => Math.max(0, prev - 1));
            closeDialog();
            setActionMessage(buildDeletionAlert({
                responseMessage: response.data?.message,
                fallbackMessage: `${businessToDelete.name} deleted successfully.`,
                warnings: response.data?.warnings,
            }));
        } catch (err) {
            closeDialog();
            setActionMessage({
                severity: 'error',
                text: err.response?.data?.message || 'Failed to delete business.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleApprove = async () => {
        try {
            setSubmitting(true);
            setActionMessage(null);
            await axios.put('/api/business-approve.php', { id: selectedBusiness.id });
            setBusinesses((prev) =>
                prev.map((business) =>
                    business.id === selectedBusiness.id ? { ...business, approved: true } : business,
                ),
            );
            setActionMessage({
                severity: 'success',
                text: `${selectedBusiness.name} approved successfully.`,
            });
            closeDialog();
        } catch (err) {
            setActionMessage({
                severity: 'error',
                text: err.response?.data?.message || 'Failed to approve business.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangePage = (_event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <AdminPageShell
                icon={<BusinessCenter />}
                eyebrow="Business directory"
                title="Manage Businesses"
                description="Loading the current business directory."
                maxWidth="xl"
            >
                <AdminSurface sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </AdminSurface>
            </AdminPageShell>
        );
    }

    if (error) {
        return (
            <AdminPageShell
                icon={<BusinessCenter />}
                eyebrow="Business directory"
                title="Manage Businesses"
                description="There was a problem loading the business directory."
                maxWidth="xl"
            >
                <Alert severity="error">{error}</Alert>
            </AdminPageShell>
        );
    }

    return (
        <AdminPageShell
            icon={<BusinessCenter />}
            eyebrow="Business directory"
            title="Manage Businesses"
            description="Browse, review, approve, remove, or send expiry reminders to businesses from a cleaner dashboard-style list."
            maxWidth="xl"
        >
            <Stack spacing={3}>
                {actionMessage && (
                    <Alert severity={actionMessage.severity} onClose={() => setActionMessage(null)}>
                        {actionMessage.text}
                    </Alert>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <AdminStat label="Businesses on page" value={businesses.length} helper="Current dataset loaded for this page." />
                    <AdminStat label="Approved" value={approvedCount} helper="Businesses already approved in the current list." />
                    <AdminStat label="Pending" value={businesses.length - approvedCount} helper="Businesses still waiting for approval." />
                    <AdminStat label="Total businesses" value={totalBusinesses} helper="All businesses available to the super admin." />
                </Stack>

                <AdminSurface sx={{ p: 0, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            px: { xs: 2, md: 3 },
                            py: 2.5,
                            borderBottom: '1px solid rgba(106, 90, 249, 0.1)',
                            background: 'linear-gradient(180deg, rgba(248,249,255,0.95) 0%, rgba(255,255,255,0.95) 100%)',
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                            Business records
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            Use the actions column to view details, delete a business, approve it when needed, or email expiry reminders.
                        </Typography>
                    </Box>

                    {isSmallScreen ? (
                        <Stack spacing={1.5} sx={{ p: 2 }}>
                            {businesses.map((business) => (
                                <AdminSurface key={business.id} sx={{ p: 2 }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#2d2a5a', overflowWrap: 'anywhere' }}>
                                                    {business.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    ID: {business.id}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={business.approved ? 'Approved' : 'Pending'}
                                                color={business.approved ? 'success' : 'warning'}
                                                variant={business.approved ? 'filled' : 'outlined'}
                                                sx={{ fontWeight: 700, flexShrink: 0 }}
                                            />
                                        </Stack>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body2" color="text.secondary">Package: {business.package}</Typography>
                                            <Typography variant="body2" color="text.secondary">Expiry: {formatExpiryDate(business.expiry)}</Typography>
                                            <Typography variant="body2" color="text.secondary">Days to expiry: {formatDaysToExpiry(business.days_to_expiry)}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Email: {business.email || 'No email on file'}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            <Button size="small" variant="outlined" onClick={() => handleView(business.id)}>View</Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                onClick={() => {
                                                    setSelectedBusiness(business);
                                                    setDialogType('delete');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Email />}
                                                disabled={notifyingBusinessId === business.id || !business.email}
                                                onClick={() => handleSendExpiryNotification(business)}
                                            >
                                                {notifyingBusinessId === business.id ? 'Sending...' : 'Notify expiry'}
                                            </Button>
                                            {!business.approved && (
                                                <Button
                                                    size="small"
                                                    color="success"
                                                    variant="contained"
                                                    onClick={() => {
                                                        setSelectedBusiness(business);
                                                        setDialogType('approve');
                                                        setOpenDialog(true);
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </AdminSurface>
                            ))}
                        </Stack>
                    ) : (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table aria-label="business table" sx={{ minWidth: 1100 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Business</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Package</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Expiry date</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Days to expiry</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700, py: 2 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {businesses.map((business) => (
                                        <TableRow key={business.id} hover>
                                            <TableCell sx={{ py: 2 }}>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2d2a5a', overflowWrap: 'anywhere' }}>
                                                        {business.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        ID: {business.id}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                                        {business.email || 'No email on file'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ py: 2 }}>{business.package}</TableCell>
                                            <TableCell sx={{ py: 2 }}>{formatExpiryDate(business.expiry)}</TableCell>
                                            <TableCell sx={{ py: 2 }}>{formatDaysToExpiry(business.days_to_expiry)}</TableCell>
                                            <TableCell sx={{ py: 2 }}>
                                                <Chip
                                                    label={business.approved ? 'Approved' : 'Pending'}
                                                    color={business.approved ? 'success' : 'warning'}
                                                    variant={business.approved ? 'filled' : 'outlined'}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 2 }}>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    <IconButton color="info" onClick={() => handleView(business.id)}>
                                                        <Visibility />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedBusiness(business);
                                                            setDialogType('delete');
                                                            setOpenDialog(true);
                                                        }}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                    <IconButton
                                                        color="primary"
                                                        disabled={notifyingBusinessId === business.id || !business.email}
                                                        onClick={() => handleSendExpiryNotification(business)}
                                                        title="Send expiry notification"
                                                    >
                                                        <Email />
                                                    </IconButton>
                                                    {!business.approved && (
                                                        <IconButton
                                                            color="success"
                                                            onClick={() => {
                                                                setSelectedBusiness(business);
                                                                setDialogType('approve');
                                                                setOpenDialog(true);
                                                            }}
                                                        >
                                                            <CheckCircle />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={totalBusinesses}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Rows per page"
                        sx={{
                            borderTop: '1px solid rgba(106, 90, 249, 0.1)',
                            '& .MuiTablePagination-toolbar': {
                                flexWrap: 'wrap',
                                gap: 1,
                                px: { xs: 2, md: 3 },
                            },
                        }}
                    />
                </AdminSurface>
            </Stack>

            <Dialog open={openDialog} onClose={submitting ? undefined : closeDialog} fullWidth maxWidth="xs">
                <DialogTitle>{dialogType === 'delete' ? 'Confirm Delete' : 'Confirm Approval'}</DialogTitle>
                <DialogContent>
                    {dialogType === 'delete' ? (
                        <Stack spacing={2}>
                            <Alert
                                severity="warning"
                                icon={<WarningAmber fontSize="inherit" />}
                                sx={{ alignItems: 'flex-start' }}
                            >
                                Deleting a business permanently removes its users, records, payment history, and any linked record files. Missing files may still allow the delete to finish with warnings.
                            </Alert>
                            <Typography variant="body2" color="text.secondary">
                                Are you sure you want to delete <strong>{selectedBusiness?.name}</strong>? This action cannot be undone.
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Approve {selectedBusiness?.name}?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={dialogType === 'delete' ? handleDelete : handleApprove}
                        color={dialogType === 'delete' ? 'error' : 'success'}
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? 'Working...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPageShell>
    );
};

export default BusinessList;
